<template>
  <form>
    <VicePanel class="step-event-form">
      <template v-slot:header>
        <span @dblclick="fillFormWithDummyData" class="select-none">Basic information*</span>
      </template>
      <div class="w-full space-y-6">
        <div class="grid grid-cols-5 gap-x-3 md:gap-x-6">
          <div class="col-span-4">
            <div class="grid grid-cols-2 gap-x-6 mb-6" v-show="showUniAndClinicianSelectBlock">
              <div :class="uniClass">
                <jet-label for="university_id" value="Select organization*" />
                <select
                  @change="universityChanged"
                  data-cy="user-register-university"
                  id="university_id"
                  name="university_id"
                  class="
                    mt-1
                    block
                    w-full
                    border-gray-300
                    focus:ring-vice-teal-500
                    focus:border-vice-teal-500
                    rounded-md
                    shadow-sm
                    sm:text-sm
                    font-medium
                    text-gray-900
                  "
                  :disabled="$page.props.forCourseId"
                  :class="{ 'opacity-50 cursor-not-allowed': $page.props.forCourseId }"
                  v-model="form.university_id"
                >
                  <option value="" disabled>Select organization</option>
                  <option v-for="uni in this.universityOptions" :key="uni.id" :value="uni.id">
                    {{ uni.name }}
                  </option>
                </select>
              </div>
              <div class="" v-show="showClinicianSelect">
                <SelectWithAvatar
                  :options="clinicianOptions"
                  label="Select clinician*"
                  :selectedOption="this.form.clinician_id"
                  @optionSelected="clinicianChanged"
                ></SelectWithAvatar>
              </div>
            </div>
            <div :class="{ 'grid grid-cols-2': $page.props.forCourseId }" class="gap-x-6">
              <div class="block text-sm font-medium text-gray-700 mb-1">
                Session access
                <!-- Session access -->
                <div class="mt-1 grid gap-3 grid-cols-2">
                  <label
                    for="session_access_private"
                    :class="{
                      'border-vice-teal-400 bg-vice-teal-400': form.is_private,
                      'border-gray-300': !form.is_private,
                    }"
                    class="
                      hover:bg-vice-teal-400
                      bg-opacity-5
                      hover:bg-opacity-5
                      border
                      rounded-md
                      w-full
                      p-2.5
                      py-1.5
                      flex
                      items-center
                      gap-2
                      cursor-pointer
                      shadow-sm
                    "
                  >
                    <input
                      id="session_access_private"
                      v-model="form.is_private"
                      :value="1"
                      name="session_access"
                      type="radio"
                      class="
                        h-4
                        w-4
                        border-gray-300
                        text-vice-teal-400
                        outline-none
                        ring-0
                        focus:outline-none
                        focus:ring-0
                      "
                    />
                    <div class="flex w-full items-center">
                      <div class="w-3/4">
                        <span class="block text-sm font-medium leading-6 text-gray-900">
                          Private session
                        </span>
                      </div>
                      <div class="w-1/4">
                        <div class="flex justify-end">
                          <LockClosedIcon class="v-5 h-5 grey-icon" />
                        </div>
                      </div>
                    </div>
                  </label>
                  <label
                    for="session_access_public"
                    :class="{
                      'border-vice-teal-400 bg-vice-teal-400': !form.is_private,
                      'border-gray-300': form.is_private,
                    }"
                    class="
                      hover:bg-vice-teal-400
                      bg-opacity-5
                      hover:bg-opacity-5
                      border
                      rounded-md
                      w-full
                      p-2.5
                      py-1.5
                      flex
                      items-center
                      gap-2
                      cursor-pointer
                      shadow-sm
                    "
                  >
                    <input
                      v-model="form.is_private"
                      id="session_access_public"
                      name="session_access"
                      type="radio"
                      :value="0"
                      class="
                        h-4
                        w-4
                        border-gray-300
                        text-vice-teal-400
                        outline-none
                        ring-0
                        focus:outline-none
                        focus:ring-0
                      "
                    />
                    <div class="flex w-full items-center">
                      <div class="w-3/4">
                        <span class="block text-sm font-medium leading-6 text-gray-900">
                          Institutional session
                        </span>
                      </div>
                      <div class="w-1/4">
                        <div class="flex justify-end">
                          <div class="w-1/4">
                            <div class="flex justify-end">
                              <InfoTooltip width="360">
                                <template v-slot:info-header>Institutional session</template>
                                <template v-slot:info-content>
                                  <p>
                                    A session shared across your institution, allowing all learners
                                    to discover it on their dashboard and request to take part.
                                  </p>
                                </template>
                              </InfoTooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <!-- Learning Course -->
              <div v-if="$page.props.forCourseId">
                <jet-label for="learning_course_id" value="Course" />
                <select
                  @change="courseChanged"
                  data-cy="create_session_course_id"
                  id="learning_course_id"
                  name="learning_course_id"
                  :disabled="$page.props.forCourseId"
                  class="
                    mt-1
                    block
                    w-full
                    border-gray-300
                    focus:ring-vice-teal-500
                    focus:border-vice-teal-500
                    rounded-md
                    shadow-sm
                    sm:text-sm
                    font-medium
                    text-gray-900
                    opacity-50
                    cursor-not-allowed
                  "
                  v-model="this.form.learning_course_id"
                >
                  <option value="" disabled>Select organization</option>
                  <option v-for="course in courseOptions" :key="course.id" :value="course.id">
                    {{ course.title }}
                  </option>
                </select>
              </div>

              <div class="col-span-2 mt-6">
                <label for="session_title" class="block text-sm font-medium text-gray-700">
                  Session title
                </label>
                <input
                  data-cy="session-title"
                  type="text"
                  name="title"
                  id="session_title"
                  v-model="form.title"
                  autocomplete="session_title"
                  autofocus
                  class="
                    mt-1
                    focus:ring-vice-teal-500
                    focus:border-vice-teal-500
                    block
                    w-full
                    shadow-sm
                    sm:text-sm
                    border-gray-300
                    rounded-md
                  "
                />
                <jet-input-error :message="$page.props.errors.title" class="mt-2" />
              </div>
            </div>
          </div>
          <div>
            <session-image @imageChanged="changedImage" @imageRemoved="removeImage"></session-image>
          </div>
        </div>
      </div>
    </VicePanel>

    <VicePanel class="step-event-form">
      <template v-slot:header>
        <span class="select-none step-date-time">Date and time*</span>
      </template>
      <div class="w-full space-y-6">
        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-7 md:col-span-3">
            <label for="session_date" class="block text-sm font-medium text-gray-700">
              Session date
            </label>
            <date-picker
              ref="datepicker"
              v-model="form.session_date"
              :min-date="new Date()"
              :popover="{ visibility: 'click' }"
              :model-config="datepickerConfig"
              :masks="{ weekdays: 'WWW', input: 'DD MMMM YYYY' }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="input-with-icon">
                  <input
                    data-cy="session-date"
                    ref="date"
                    name="session_date"
                    id="session_date"
                    class="
                      mt-1
                      focus:outline-none
                      focus:ring-vice-teal-500
                      focus:border-vice-teal-500
                      focus:ring-1
                      w-full
                      p-2
                      border border-gray-300
                      shadow-sm
                      text-sm
                      rounded-md
                    "
                    placeholder="Select a date"
                    v-on="inputEvents"
                    :value="inputValue"
                  />
                  <CalendarIcon class="calendar-icon" />
                </div>
              </template>
            </date-picker>
            <jet-input-error :message="$page.props.errors.session_date" class="mt-2" />
          </div>

          <div class="col-span-5 md:col-span-2 relative">
            <label for="session_start" class="block text-sm font-medium text-gray-700">
              Start time
            </label>
            <div class="mt-1" :class="{ 'opacity-50 pointer-events-none': !form.session_date }">
              <ClockIcon class="clock-icon"></ClockIcon>

              <date-picker
                data-cy="select-time"
                ref="startTime"
                id="session_start"
                v-model="sessionStart"
                mode="time"
                hide-time-header
                :model-config="timepickerConfig"
                is24hr
                placeholder="Start date"
                name="session_start"
                class="mt-1 focus:ring-vice-teal-500 focus:ring-1 block min-w-full shadow-sm"
              ></date-picker>
            </div>
            <jet-input-error :message="$page.props.errors.session_start" class="mt-2" />
          </div>

          <div class="col-span-5 md:col-span-3">
            <label for="session_duration" class="block text-sm font-medium text-gray-700">
              Session duration (mins)
            </label>
            <div class="input-with-icon">
              <input
                data-cy="session-duration"
                v-model="form.session_duration"
                id="session_duration"
                placeholder="Session duration"
                @input="
                  form.session_duration =
                    !!form.session_duration && Math.abs(form.session_duration) >= 0
                      ? Math.abs(form.session_duration)
                      : null
                "
                name="session_duration"
                type="number"
                step="5"
                class="
                  mt-1
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  block
                  w-full
                  shadow-sm
                  sm:text-sm
                  border-gray-300
                  rounded-md
                "
              />
              <HourGlass class="calendar-icon" />
            </div>
            <jet-input-error :message="$page.props.errors.session_duration" class="mt-2" />
          </div>

          <div class="col-span-7 md:col-span-4">
            <TimeZoneSelect @timezoneChanged="changeTimezone" :selectedTimezone="form.timezone" />
            <jet-input-error :message="$page.props.errors.timezone" class="mt-2" />
          </div>
        </div>
      </div>
    </VicePanel>

    <VicePanel v-if="!form.is_private">
      <template v-slot:header>Attendance preferences</template>
      <div class="w-full space-y-1">
        <div class="grid grid-cols-10 gap-6">
          <div
            class="col-span-4 sm:col-span-5"
            :class="
              form.participant_role_id === 1 || form.participant_role_id === 2 ? 'block' : 'hidden'
            "
          >
            <div>
              <label for="max_participants" class="block text-sm font-medium text-gray-700">
                Max. amount of participants
              </label>
              <div
                class="mt-1 relative rounded-md"
                :class="{ 'shadow-sm': !$page.props.errors.max_participants_amount }"
              >
                <div
                  class="
                    absolute
                    inset-y-0
                    left-1.5
                    top-1
                    pl-1.5
                    flex
                    items-center
                    pointer-events-none
                    h-8
                    w-8
                    rounded-full
                    object-cover
                    bg-vice-light-blue
                  "
                >
                  <UserIcon class="h-5 w-5 text-vice-medium-blue" aria-hidden="true" />
                </div>
                <input
                  data-cy="maximum-participants"
                  type="number"
                  name="max_participants"
                  id="max_participants"
                  class="
                    focus:ring-vice-teal-500
                    focus:border-vice-teal-500
                    block
                    w-full
                    pl-12
                    sm:text-sm
                    border-gray-300
                    rounded-md
                    py-2.5
                  "
                  v-model="form.max_participants_amount"
                  min="0"
                  max="20"
                  @change="checkParticipantMaxLimitation"
                />
                <jet-input-error
                  :message="$page.props.errors.max_participants_amount"
                  class="mt-2"
                />
              </div>
            </div>
          </div>
          <div
            class="col-span-4 sm:col-span-5"
            :class="
              form.participant_role_id === 1 || form.participant_role_id === 3 ? 'block' : 'hidden'
            "
          >
            <label for="max_observers" class="block text-sm font-medium text-gray-700">
              Max. amount of observers
            </label>
            <div
              class="mt-1 relative rounded-md"
              :class="{ 'shadow-sm': !$page.props.errors.max_observers_amount }"
            >
              <div
                class="
                  absolute
                  inset-y-0
                  left-1.5
                  top-1
                  pl-1.5
                  flex
                  items-center
                  pointer-events-none
                  h-8
                  w-8
                  rounded-full
                  object-cover
                  bg-vice-light-yellow
                "
              >
                <EyeIcon class="h-5 w-5 text-vice-dark-yellow" aria-hidden="true" />
              </div>
              <input
                data-cy="maximum-observers"
                type="number"
                name="max_observers"
                id="max_observers"
                class="
                  focus:ring-vice-teal-500
                  focus:border-vice-teal-500
                  block
                  w-full
                  pl-12
                  sm:text-sm
                  border-gray-300
                  rounded-md
                  py-2.5
                "
                v-model="form.max_observers_amount"
                min="0"
                :max="MAX_OBSERVERS_AMOUNT"
                @change="checkObserverMaxLimitation"
              />
              <jet-input-error :message="$page.props.errors.max_observers_amount" class="mt-2" />
            </div>
          </div>
        </div>
        <div class="grid grid-cols-10 gap-6">
          <div class="col-span-10 sm:col-span-5">
            <label for="session_title" class="block text-sm font-medium text-gray-500">
              Participants can talk to you and send messages
            </label>
          </div>
          <div
            class="col-span-10 sm:col-span-5"
            :class="
              form.participant_role_id === 1 || form.participant_role_id === 2 ? 'block' : 'hidden'
            "
          >
            <label for="session_title" class="block text-sm font-medium text-gray-500">
              Observers can interact with you during the session through chat messages
            </label>
          </div>
        </div>
      </div>
    </VicePanel>

    <VicePanel class="step-user-invite">
      <template v-slot:header>
        <div class="flex w-full items-center">
          <div class="w-1/3">
            Participants and observers*
            <InfoTooltip width="600">
              <template v-slot:info-header>About participants and observers</template>
              <template v-slot:info-content>
                <p class="mb-6">
                  <strong>Participants:</strong>
                  Join with video, voice and chat and are visible on screen. Maximum 20 per session.
                </p>
                <p class="mb-6">
                  <strong>Observers:</strong>
                  Join the session and can use chat, but do not appear on screen. Up to 250 per
                  session.
                </p>
                <p>
                  <strong>Inviting students:</strong>
                  Select students from your university or enter an email address. You can also
                  upload a CSV.
                </p>
              </template>
            </InfoTooltip>
          </div>
          <div class="w-2/3">
            <div class="flex justify-end items-center gap-3">
              <div class="flex rounded-md shadow-sm" role="group">
                <button
                  type="button"
                  data-cy="session-type-live-online"
                  @click="form.session_type = 'live_online'"
                  :class="
                    form.session_type === 'live_online'
                      ? 'bg-vice-teal-500 text-white border-vice-teal-500'
                      : 'bg-white text-gray-700 border-gray-300'
                  "
                  class="
                    flex
                    items-center
                    gap-1
                    px-5
                    py-1.5
                    text-xs
                    font-medium
                    border
                    rounded-l-md
                    focus:outline-none
                  "
                >
                  Live online
                </button>
                <button
                  type="button"
                  data-cy="session-type-in-classroom"
                  @click="form.session_type = 'in_classroom'"
                  :class="
                    form.session_type === 'in_classroom'
                      ? 'bg-vice-teal-500 text-white border-vice-teal-500'
                      : 'bg-white text-gray-700 border-gray-300'
                  "
                  class="
                    flex
                    items-center
                    gap-2
                    px-3
                    py-1.5
                    text-xs
                    font-medium
                    border border-l-0
                    rounded-r-md
                    focus:outline-none
                  "
                >
                  In classroom
                  <InfoTooltip
                    :fill="form.session_type === 'in_classroom' ? '#fff' : '#0D9488'"
                    width="360"
                  >
                    <template v-slot:info-header>In classroom</template>
                    <template v-slot:info-content>
                      Learners watch the session together on a classroom screen and can optionally
                      use their own devices for chat and reflection.
                    </template>
                  </InfoTooltip>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="block">
        <div
          v-if="form.session_type === 'in_classroom'"
          class="
            w-full
            inline-flex
            gap-6
            p-4
            mb-6
            rounded-lg
            shadow-sm
            border border-vice-teal-500 border-opacity-30
            bg-vice-teal-400 bg-opacity-5
          "
        >
          <div class="w-2/3">
            <label for="room_location" class="block text-sm font-medium text-gray-500 uppercase">
              Room / location
            </label>
            <input
              data-cy="room-location"
              type="text"
              name="room_location"
              id="room_location"
              v-model="form.room_location"
              placeholder="e.g. Lecture Theatre 2, Aberdeen"
              class="
                mt-1
                focus:ring-vice-teal-500
                focus:border-vice-teal-500
                block
                w-full
                shadow-sm
                sm:text-sm
                border-gray-300
                rounded-md
              "
            />
            <p class="mt-1 text-xxs text-gray-500">
              Shown in the invite so learners know where to go.
            </p>
            <jet-input-error :message="$page.props.errors.room_location" class="mt-2" />
          </div>
          <div class="w-1/3 border border-vice-teal-500 border-opacity-30 bg-white p-4 rounded-lg">
            <h6 class="block text-xs font-medium">What classroom attendees get</h6>
            <ul class="mt-2 text-xxs text-gray-500 list-disc list-inside">
              <li>Chat with the facilitator & clinician</li>
              <li>Write a reflection</li>
            </ul>
          </div>
        </div>
        <div class="w-full">
          <div class="grid grid-cols-2 gap-3 md:gap-6">
            <!-- :max-amount="form.max_participants_amount" -->
            <UserInvitationList
              user-type="participant"
              :sessionType="form.session_type"
              :max-amount="20"
              :taken-users="allInvitedUsers"
              :clinician-university-students="filteredStudents"
              @userEmailsChanged="changeParticipantEmails"
            />
            <!-- :max-amount="form.max_observers_amount" -->
            <UserInvitationList
              user-type="observer"
              :sessionType="form.session_type"
              :max-amount="MAX_OBSERVERS_AMOUNT"
              :taken-users="allInvitedUsers"
              :clinician-university-students="filteredStudents"
              @userEmailsChanged="changeObserverEmails"
            />
          </div>
        </div>
        <div class="w-full mt-6">
          <label for="" class="block text-sm font-medium text-gray-700 mb-1">
            Lock consultation
          </label>
          <div class="grid grid-cols-12">
            <div class="col-span-7">
              <span class="text-sm text-gray-700">
                For security and focus, you can lock the consultation phase to prevent anyone from
                joining after it has started.
              </span>
            </div>
            <div class="col-span-5">
              <div class="flex w-full items-center">
                <ToggleSettings
                  :enabled="!form.late_entry"
                  @toggled="toggleLateEntry"
                  class="ml-2"
                />
                <span class="block text-sm text-gray-900 ml-4">Lock consultation</span>
              </div>
            </div>
          </div>
          <jet-input-error :message="$page.props.errors.session_start" class="mt-2" />
        </div>
      </div>
    </VicePanel>

    <VicePanel class="mb-2">
      <template v-slot:header>
        <div class="">About the session</div>
      </template>
      <div class="block w-full">
        <div class="w-full">
          <MultipleFilesInput />
        </div>
        <div class="w-full mt-4">
          <LearningOutcomes @changeSessionContent="sessionContentChanged" />
        </div>
        <div class="w-full mt-4">
          <SessionDetails @changeSessionContent="sessionContentChanged" />
        </div>
      </div>
    </VicePanel>
  </form>
</template>

<script>
import axios from 'axios';
import { UserIcon, EyeIcon } from '@heroicons/vue/solid';
import { LockClosedIcon, CalendarIcon } from '@heroicons/vue/outline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DatePicker } from 'v-calendar';
import { listTz } from 'timezone-select-js';
import '@vueup/vue-quill/dist/vue-quill.snow.prod.css';
import { MAX_CHAR_LENGTH, MAX_OBSERVERS_AMOUNT } from '@/Utils/vice-constants';
import 'v-calendar/dist/style.css';
import '../../../../../css/date-picker.css';

import UserInvitationList from '@/Pages/Clinician/Session/Forms/Inputs/UserInvitationList.vue';
import VicePanel from '@/Components/Panels/VicePanel.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import SelectWithAvatar from '@/Components/Inputs/SelectWithAvatar.vue';
import TimeZoneSelect from './Inputs/TimeZoneSelect.vue';

import SessionTabs from './Mixins/SessionTabs.vue';
// import YearOfStudy from './Inputs/YearOfStudy.vue';
import Input from '@/Jetstream/Input.vue';
import SessionImage from './Inputs/SessionImage.vue';
import MultipleFilesInput from './Inputs/MultipleFilesInput.vue';

// eslint-disable-next-line import/no-extraneous-dependencies
import 'flatpickr/dist/flatpickr.css';
import ToggleSettings from '@/Components/Inputs/ToggleSettings.vue';
import JetLabel from '@/Jetstream/Label.vue';
import HourGlass from '@/Components/Icons/HourGlass.vue';
import SessionDetails from './EventForms/SessionDetails.vue';
import InfoTooltip from '@/Components/Tooltips/InfoTooltip.vue';
import ClockIcon from '@/Components/Icons/ClockIcon.vue';
import LearningOutcomes from '@/Pages/Clinician/Session/Forms/EventForms/LearningOutcomes.vue';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export default {
  name: 'EventForm',
  components: {
    LearningOutcomes,
    JetLabel,
    ToggleSettings,
    MultipleFilesInput,
    VicePanel,
    SessionImage,
    DatePicker,
    // eslint-disable-next-line vue/no-unused-components
    Input,
    // YearOfStudy,
    SelectWithAvatar,
    JetInputError,
    UserIcon,
    EyeIcon,
    TimeZoneSelect,
    SessionDetails,
    UserInvitationList,
    LockClosedIcon,
    CalendarIcon,
    HourGlass,
    InfoTooltip,
    ClockIcon,
  },
  mixins: [SessionTabs],

  mounted() {
    if (this.$page.props.session) {
      // if the session is not new, we need to set the session start date
      if (dayjs(this.$page.props.session.created_at) < dayjs().subtract(10, 'seconds')) {
        this.sessionStart = new Date(`${this.form.session_date} ${this.form.session_start}:00`);
      }

      axios.post(route('user.invite.list', this.$page.props.session.id)).then((response) => {
        this.participant_list = response.data.participant_list;
        this.observer_list = response.data.observer_list;
      });
    }

    if (!this.$page.props.isAdmin) {
      this.fetchUniversitiesByClinician(this.$page.props.user.id);
    } else {
      this.fetchUniversitiesByClinician(this.form.clinician_id);
    }
  },
  data() {
    return {
      observer_list: '',
      participant_list: '',
      form: this.$inertia.form(this.formData()),

      date: null,
      sessionStart: dayjs().add(1, 'hour').toDate(),

      timezone_list: listTz(),
      endTimeConfig: {
        altInput: true,
        noCalendar: true,
        enableTime: true,
        defaultDate: null,
      },
      showTimezone: false,
      datepickerConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD',
      },
      timepickerConfig: {
        type: 'string',
        mask: 'HH:mm',
      },
      MAX_OBSERVERS_AMOUNT,
      maxSummaryCharactersCount: 500,
      summaryCharactersLeft: 500,
      uniStudents: [],
      filteredStudents: [],
      clinicianStudents: [],
      universityOptions: [],
      about_the_session: this.$page.props.session
        ? this.$page.props.session.about?.about_the_session
        : '',
      courses: this.$page.props.courses,
    };
  },
  computed: {
    clinicianOptions() {
      // If session for course, only show the clinician related to that course
      if (this.$page.props.forCourseId) {
        const course = this.$page.props.courses.find(
          (learningCourse) => learningCourse.id === parseInt(this.$page.props.forCourseId, 10),
        );

        return course.clinicians;
      }

      return this.$page.props.clinicians;
    },
    courseOptions() {
      // Only available courses for the selected clinician
      if (this.form.clinician_id) {
        const selectedClinician = this.$page.props.clinicians.find(
          (clinician) => clinician.id === this.form.clinician_id,
        );
        return selectedClinician.learning_courses;
      }
      return this.courses;
    },
    allInvitedUsers() {
      const selectedAndNewSelectedObservers = this.form.observer_list.selected_list?.concat(
        this.form.observer_list.new_selected_list,
      );
      const currentObservers =
        selectedAndNewSelectedObservers?.filter(
          (email) => !this.form.observer_list.deselected_list?.includes(email),
        ) || [];

      const selectedAndNewSelectedParticipants = this.form.participant_list.selected_list?.concat(
        this.form.participant_list.new_selected_list,
      );
      const currentParticipants =
        selectedAndNewSelectedParticipants?.filter(
          (email) => !this.form.participant_list.deselected_list?.includes(email),
        ) || [];
      // return currentObservers;
      return _.uniq(currentObservers?.concat(currentParticipants));
    },
    showUniAndClinicianSelectBlock() {
      if (this.$page.props.clinicianRole === 'admin') return true;

      return this.universityOptions.length > 1;
    },
    uniClass() {
      if (this.$page.props.clinicianRole === 'clinician' && this.universityOptions.length > 1)
        return 'col-span-2';

      return '';
    },
    showClinicianSelect() {
      return this.$page.props.clinicianRole === 'admin';
    },
  },
  watch: {
    sessionStart: {
      handler() {
        this.form.session_start = dayjs(this.sessionStart).format('HH:mm');
      },
    },
  },
  methods: {
    roundNearest5(num) {
      return Math.round(num / 5) * 5;
    },
    submitForm(status) {
      this.form.status = status;
      if (typeof this.form.image === 'string') {
        this.form.image = null;
      }

      this.form.session_date = dayjs(this.form.session_date).format('YYYY-MM-DD');
      const routeParams = {
        unique_id: this.$page.props.session.unique_id,
        course_id: this.$page.props.forCourseId,
      };
      this.form.post(this.route('update.session', routeParams));
    },

    yearFormatter(date) {
      return dayjs(date).format('YYYY-MM-DD');
    },
    hoursFormatter(date) {
      return dayjs(date).format('HH:mm');
    },

    changeTimezone(selectedTimezone) {
      this.form.timezone = selectedTimezone;
    },

    changingSessionContent(type) {
      this.$emit('changeSessionContent', { type, content: this[type] });
    },
    limitCharacters(type) {
      const quill = this.$refs[type].getQuill();
      if (quill.getLength() > MAX_CHAR_LENGTH) {
        quill.deleteText(MAX_CHAR_LENGTH, quill.getLength());
      }
    },

    formData() {
      return {
        title: this.$page.props.session.title,
        summary: this.$page.props.session.summary,
        image: this.$page.props.session.details ? this.$page.props.session.details.image : '',
        image_has_changed: false,
        session_date: this.$page.props.session.details
          ? this.yearFormatter(this.$page.props.session.details.session_start)
          : null,
        session_start: this.$page.props.session.details
          ? this.hoursFormatter(this.$page.props.session.details.session_start)
          : null,
        session_duration: this.$page.props.session.details
          ? this.$page.props.session.details.duration
          : null,
        timezone:
          this.$page.props.session.details.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        year_of_study: this.$page.props.session.details
          ? this.$page.props.session.details.year_of_study
          : null,
        status: this.$page.props.session.status,
        is_private: this.$page.props.session.is_private,
        late_entry: !!this.$page.props.session.late_entry,
        session_type: this.$page.props.session.details
          ? this.$page.props.session.details.session_type || 'live_online'
          : 'live_online',
        room_location: this.$page.props.session.details
          ? this.$page.props.session.details.room_location || ''
          : '',
        observer_list: {},
        participant_list: {},
        clinician_id: this.$page.props.session.details.clinician_id
          ? this.$page.props.session.details.clinician_id
          : this.$page.props.clinicians[0]?.id ?? null,
        university_id: this.$page.props.session.details.university_id
          ? this.$page.props.session.details.university_id
          : null,
        learning_course_id: this.$page.props.session.learning_course_id
          ? this.$page.props.session.learning_course_id
          : null,
        participant_permission_id: this.$page.props.session.participant_preferences
          ? this.$page.props.session.participant_preferences.participant_permission_id
          : null,
        participant_role_id: this.$page.props.session.participant_preferences
          ? this.$page.props.session.participant_preferences.participant_role_id
          : null,
        max_participants_amount: this.$page.props.session.participant_preferences
          ? this.$page.props.session.participant_preferences.max_participants_amount
          : null,
        max_observers_amount: this.$page.props.session.participant_preferences
          ? this.$page.props.session.participant_preferences.max_observers_amount
          : null,
        about_the_session: this.$page.props.session.about
          ? this.$page.props.session.about.about_the_session
          : '',
        about_the_clinic: this.$page.props.session.about
          ? this.$page.props.session.about.about_the_clinic
          : '',
        what_you_will_learn: this.$page.props.session.about
          ? this.$page.props.session.about.what_you_will_learn
          : '',
      };
    },
    clinicianChanged(event) {
      this.form.clinician_id = event.id;
      if (!this.$page.props.forCourseId) {
        this.fetchUniversitiesByClinician(event.id);
      }
    },
    universityChanged(event) {
      this.filteredStudents = this.clinicianStudents.filter(
        (user) => user.university_id === parseInt(event.target.value, 10),
      );
    },
    courseChanged(event) {
      console.log('Course changed', event.target.value);
    },
    changedImage(images) {
      this.form.image = images[0];
      this.form.image_has_changed = true;
    },
    toggleLateEntry() {
      this.form.late_entry = !this.form.late_entry;
    },
    removeImage() {
      this.form.image = '';
      this.form.image_has_changed = true;
    },

    changeParticipantPreferences(event) {
      console.log('changeParticipantPreferences');

      const { id, type } = event;
      if (type === 'role') {
        this.form.participant_role_id = id;
      } else {
        this.form.participant_permission_id = id;
      }
      if (id === 2) {
        this.form.max_observers_amount = 0;
      }
      if (id === 3) {
        this.form.max_participants_amount = 0;
      }
    },

    changeObserverEmails(event) {
      Object.assign(this.form.observer_list, event);
      this.updateFilteredStudents();
    },

    changeParticipantEmails(event) {
      Object.assign(this.form.participant_list, event);
      this.updateFilteredStudents();
    },

    updateFilteredStudents() {
      this.filteredStudents = this.uniStudents.filter(
        (item) => !this.allInvitedUsers.includes(item.email),
      );
    },

    checkObserverMaxLimitation() {
      const observerListLength = this.form.max_observers_amount.selected_list
        ? this.form.max_observers_amount.selected_list.length
        : this.observer_list.length;

      if (this.form.max_observers_amount > MAX_OBSERVERS_AMOUNT) {
        this.$page.props.errors.max_observers_amount = `Maximum number of observers is ${MAX_OBSERVERS_AMOUNT}.`;
        this.form.max_observers_amount = MAX_OBSERVERS_AMOUNT;
      } else {
        this.$page.props.errors.max_observers_amount = '';
      }
      if (observerListLength) {
        if (observerListLength > this.form.max_observers_amount) {
          this.$page.props.errors.max_observers_amount =
            'Maximum amount should be larger than observers current list';
          this.form.max_observers_amount = observerListLength;
          throw this.$page.props.errors.max_observers_amount;
        }
      }
    },
    checkParticipantMaxLimitation() {
      const participantListLength = this.form.participant_list.selected_list
        ? this.form.participant_list.selected_list.length
        : this.participant_list.length;

      if (this.form.max_participants_amount > 20) {
        this.$page.props.errors.max_participants_amount = 'Maximum number of participants is 20.';
        this.form.max_participants_amount = 20;
      } else {
        this.$page.props.errors.max_participants_amount = '';
      }

      if (participantListLength) {
        if (participantListLength > this.form.max_participants_amount) {
          this.$page.props.errors.max_participants_amount =
            'Maximum amount should be larger than participants current list';
          this.form.max_participants_amount = participantListLength;
          throw this.$page.props.errors.max_participants_amount;
        }
      }
    },
    limitValidation(event, ref, maxAmount, type) {
      if (event.selected_list) {
        if (event.selected_list.length > maxAmount) {
          ref.users_emails.pop();
          this.$page.props.errors[`${type}_email_list`] = `You can\`t add more ${type} than limit`;
          throw ref.validationError.email_list;
        } else {
          this.$page.props.errors[`${type}_email_list`] = '';
        }
      }
    },
    tabState(id) {
      this.tabs.forEach((tab) => {
        tab.current = tab.id === id;
      });
    },
    sessionContentChanged(event) {
      const { type, content } = event;
      this.form[type] = content;
    },
    changeYearOfStudy(years) {
      this.form.year_of_study = years;
    },
    validateEmail(email, ref, type) {
      if (!email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)) {
        ref.users_emails.pop();
        this.$page.props.errors[`${type}_email_list`] = 'The email must be a valid email address.';
        throw ref.validationError.email_list;
      }
    },
    fillFormWithDummyData() {
      this.form.title = `Test session #${dayjs().format('ms')}`;
      this.form.summary = 'Lorem ispum dolor sit amet';
      this.form.session_date = dayjs().format('YYYY-MM-DD');
      this.form.session_start = dayjs().add(1, 'hour').format('HH:mm');
      this.form.session_end = dayjs().add(2, 'hour').format('HH:mm');
      this.form.max_participants_amount = 5;
      this.form.participant_list = {
        deselected_list: [],
        new_selected_list: [],
        selected_list: [],
      };
    },
    inputSubscription(event) {
      const summaryLength = event.target.value.length;
      this.summaryCharactersLeft = summaryLength
        ? this.maxSummaryCharactersCount - summaryLength
        : this.maxSummaryCharactersCount;
    },

    fetchUniversitiesByClinician(clinicianId) {
      if (typeof clinicianId == 'undefined') return;

      axios
        .get(route('clinicians.universities', { clinicianId }))
        .then((response) => {
          this.universityOptions = response?.data?.flatMap((uni) => [
            { id: uni.id, name: uni.name },
          ]);

          // this.showUniAndClinicianSelect();

          this.form.university_id = this.universityOptions[0]?.id;
          const students = response?.data?.flatMap(({ users }) => users);
          this.clinicianStudents = students;

          this.uniStudents =
            students?.filter((user) => user.university_id === this.form.university_id) || [];
          this.filteredStudents = this.uniStudents.filter(
            (item) => !this.allInvitedUsers.includes(item.email),
          );
        })
        .catch((error) => console.error('Error fetching universities:', error));
    },
  },
};
</script>
<style scoped>
.grey-icon {
  color: #9ca3af;
}

.input-with-icon {
  position: relative;
}

.input-with-icon input {
  padding-left: 35px; /* Make room for the icon */
}

.calendar-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #656262;
}

.clock-icon {
  position: absolute;
  z-index: 1;
  top: 33px;
  left: 10px;
}
</style>
